import { forwardRef } from 'react'

export interface ShareHoroscopeData {
  date?: string
  career?: string | null
  finance?: string | null
  love?: string | null
  health?: string | null
  lucky_color?: string
  work_color?: string
  love_color?: string
  finance_color?: string
  health_color?: string
  bad_luck_color?: string
  overall_score?: number
}

// Hardcoded values — CSS vars don't resolve inside html-to-image canvas
const C = {
  bg:          '#16120d',
  bg2:         '#221a0f',
  bg3:         '#1c1508',
  gold:        '#d4b884',
  goldMid:     '#b8945a',
  goldDeep:    '#8a6e40',
  text:        '#f0ead8',
  textMuted:   '#b8b09a',
  textSoft:    '#7a7260',
  surface:     'rgba(255,255,255,0.06)',
  border:      'rgba(212,184,132,0.20)',
  borderLight: 'rgba(212,184,132,0.12)',
  danger:      '#d47670',
}

const COLOR_HEX: Record<string, string> = {
  'แดง': '#e53e3e', 'ส้ม': '#ed8936', 'เหลือง': '#d69e2e',
  'ทอง': '#b8945a', 'เขียว': '#38a169', 'น้ำเงิน': '#3182ce',
  'ฟ้า': '#63b3ed', 'ม่วง': '#805ad5', 'ชมพู': '#d53f8c',
  'ขาว': '#e8e0d0', 'ดำ': '#2d3748', 'เทา': '#718096',
  'น้ำตาล': '#975a16', 'ครีม': '#e8dcaa',
}

function colorHex(name?: string): string {
  if (!name) return '#b8945a'
  for (const [k, v] of Object.entries(COLOR_HEX)) {
    if (name.includes(k)) return v
  }
  return '#b8945a'
}

function formatThaiDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์']
  const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
  return `วัน${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
}


const CATEGORIES = [
  { key: 'career',  emoji: '⚜️', label: 'การงาน',  accent: '#c49030' },
  { key: 'finance', emoji: '💎', label: 'การเงิน',  accent: '#9f7ae8' },
  { key: 'love',    emoji: '🌹', label: 'ความรัก', accent: '#e05575' },
  { key: 'health',  emoji: '🌿', label: 'สุขภาพ',  accent: '#3daa66' },
]

// Renders at 540×960 px — caller uses toPng(ref, { pixelRatio: 2 }) → 1080×1920
export const HoroscopeShareCard = forwardRef<HTMLDivElement, { data: ShareHoroscopeData }>(
  ({ data }, ref) => {
    const score = data.overall_score ?? 0
    const scoreColor = score >= 8 ? '#3daa66' : score >= 6 ? '#b8945a' : '#d47670'
    const scorePct = Math.round((score / 10) * 100)
    const scoreMood = score >= 8 ? '✨ ดีมาก โชคหนุนนำ' : score >= 6 ? '🌟 ดี มีโอกาสรออยู่' : '⚡ ระมัดระวัง ใช้สติ'

    const colorItems = [
      { label: 'มงคล',    value: data.lucky_color,    main: true,    danger: false },
      { label: 'งาน',     value: data.work_color,     main: false,   danger: false },
      { label: 'ความรัก', value: data.love_color,     main: false,   danger: false },
      { label: 'การเงิน', value: data.finance_color,  main: false,   danger: false },
      { label: 'สุขภาพ',  value: data.health_color,   main: false,   danger: false },
      { label: 'กาลกิณี', value: data.bad_luck_color, main: false,   danger: true  },
    ]

    return (
      <div
        ref={ref}
        style={{
          width: '540px',
          height: '960px',
          background: `linear-gradient(155deg, ${C.bg} 0%, ${C.bg2} 45%, ${C.bg3} 70%, ${C.bg} 100%)`,
          fontFamily: '"Noto Sans Thai", "Prompt", "Sarabun", system-ui, sans-serif',
          color: C.text,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Radial gold glow top */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '300px', pointerEvents: 'none',
          background: 'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(212,184,132,0.14) 0%, transparent 70%)',
        }} />
        {/* Top accent line */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
          background: `linear-gradient(90deg, transparent 0%, ${C.gold} 30%, ${C.gold} 70%, transparent 100%)`,
        }} />
        {/* Bottom accent line */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px',
          background: `linear-gradient(90deg, transparent 0%, ${C.goldMid} 30%, ${C.goldMid} 70%, transparent 100%)`,
        }} />

        {/* ── HEADER ─────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '14px', paddingTop: '36px', paddingBottom: '18px',
          borderBottom: `1px solid ${C.border}`,
          marginLeft: '32px', marginRight: '32px',
        }}>
          <img
            src={`${window.location.origin}/logo.png`}
            width={52} height={52}
            style={{ borderRadius: '14px', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: C.gold, letterSpacing: '0.3px', lineHeight: 1.2 }}>
              Amulet Digit
            </div>
            <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
              ดูดวงฟรีทุกวัน • ทำนายฝัน • เลขมงคล
            </div>
          </div>
        </div>

        {/* ── DATE BADGE ──────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '18px' }}>
          <div style={{
            padding: '7px 28px', borderRadius: '100px',
            border: `1.5px solid ${C.goldMid}`,
            background: 'rgba(184,148,90,0.12)',
          }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: C.gold }}>
              {data.date ? formatThaiDate(data.date) : '—'}
            </span>
          </div>
        </div>

        {/* ── SCORE BLOCK ─────────────────────────────────────── */}
        <div style={{
          margin: '14px 32px 0',
          padding: '18px 22px',
          borderRadius: '20px',
          background: C.surface,
          border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: '20px',
        }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '2px' }}>คะแนนดวงวันนี้</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontSize: '54px', fontWeight: 900, lineHeight: 1, color: scoreColor }}>{score}</span>
              <span style={{ fontSize: '20px', color: C.textSoft }}>/10</span>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ height: '10px', borderRadius: '100px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: '10px' }}>
              <div style={{ height: '100%', width: `${scorePct}%`, background: scoreColor, borderRadius: '100px' }} />
            </div>
            <div style={{ fontSize: '13px', color: C.textMuted, fontWeight: 500 }}>{scoreMood}</div>
          </div>
        </div>

        {/* ── CATEGORIES 2×2 — flex:1 fills remaining space ──── */}
        <div style={{
          margin: '12px 32px 0',
          flex: 1, minHeight: 0,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '10px',
        }}>
          {CATEGORIES.map(cat => (
            <div key={cat.key} style={{
              padding: '14px 16px',
              borderRadius: '16px',
              background: C.surface,
              border: `1px solid ${C.borderLight}`,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px', flexShrink: 0 }}>
                <span style={{ fontSize: '15px' }}>{cat.emoji}</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: cat.accent }}>{cat.label}</span>
              </div>
              <p style={{
                margin: 0, fontSize: '11px', lineHeight: 1.72,
                color: C.textMuted, flex: 1, overflow: 'hidden',
              }}>
                {(data as Record<string, string | null | undefined>)[cat.key] ?? ''}
              </p>
            </div>
          ))}
        </div>

        {/* ── COLOR GRID ──────────────────────────────────────── */}
        <div style={{ margin: '12px 32px 0', flexShrink: 0 }}>
          <div style={{
            fontSize: '11px', fontWeight: 700, color: C.goldMid,
            marginBottom: '10px', letterSpacing: '0.3px',
          }}>
            สีมงคลประจำวัน
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
            {colorItems.map(c => (
              <div key={c.label} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: colorHex(c.value),
                  border: c.main
                    ? `2.5px solid ${C.gold}`
                    : c.danger
                    ? '2px solid rgba(212,118,112,0.5)'
                    : '2px solid rgba(255,255,255,0.12)',
                  boxShadow: `0 3px 10px ${colorHex(c.value)}55`,
                }} />
                <span style={{
                  fontSize: '10px', textAlign: 'center', lineHeight: 1.3,
                  color: c.danger ? C.danger : C.textMuted,
                  fontWeight: c.main ? 700 : 400,
                }}>
                  {c.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── FOOTER ──────────────────────────────────────────── */}
        <div style={{
          margin: '12px 32px 28px',
          flexShrink: 0,
          padding: '16px 20px',
          borderRadius: '18px',
          background: 'rgba(212,184,132,0.08)',
          border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '2px' }}>
              ดูดวงฟรีทุกวัน ได้ที่
            </div>
            <div style={{ fontSize: '17px', fontWeight: 800, color: C.gold }}>
              www.amuletdigit.com
            </div>
          </div>
          <div style={{
            padding: '10px 18px', borderRadius: '100px',
            background: `linear-gradient(135deg, ${C.goldMid} 0%, ${C.goldDeep} 100%)`,
            fontSize: '13px', fontWeight: 700, color: '#fff',
            boxShadow: '0 4px 16px rgba(184,148,90,0.4)',
          }}>
            ดูดวงเลย ✨
          </div>
        </div>
      </div>
    )
  }
)
HoroscopeShareCard.displayName = 'HoroscopeShareCard'
